import type { ErrorCode, PortalMe, PortalSession } from '@pm/schemas';
import { createContext, useContext } from 'react';
import { ApiRequestError } from '@/lib/api';

const TOKEN_KEY = 'pm.portal.token';
const ME_KEY = 'pm.portal.me';

/** Sessão do portal: token JWT (12h) em localStorage, sem refresh (o contato faz login de novo). */
export const portalSession = {
  get token(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  get me(): PortalMe | null {
    try {
      const raw = localStorage.getItem(ME_KEY);
      return raw ? (JSON.parse(raw) as PortalMe) : null;
    } catch {
      return null;
    }
  },
  set(session: PortalSession): void {
    try {
      localStorage.setItem(TOKEN_KEY, session.token);
      const { token: _t, expires_in: _e, ...me } = session;
      localStorage.setItem(ME_KEY, JSON.stringify(me));
      // Sobrevive ao "Sair": a tela de login do portal mostra o logotipo certo na próxima vez.
      localStorage.setItem('pm.portal.org', session.organization.id);
    } catch {
      /* modo privado: sessão só em memória nesta aba */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ME_KEY);
    } catch {
      /* ignore */
    }
  },
};

let onUnauthorized: (() => void) | null = null;
export function setPortalUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const token = portalSession.token;
  const res = await fetch(`/api/v1/portal${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { code: ErrorCode; message: string; details?: Record<string, unknown> };
  } | null;
  if (!res.ok) {
    if ((res.status === 401 || res.status === 403) && !path.startsWith('/auth/')) {
      portalSession.clear();
      onUnauthorized?.();
    }
    throw new ApiRequestError(
      res.status,
      json?.error?.code ?? 'INTERNAL_ERROR',
      json?.error?.message ?? `Erro ${res.status}`,
      json?.error?.details,
    );
  }
  return json?.data as T;
}

export const portalApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}, headers?: Record<string, string>) =>
    request<T>('POST', path, body, headers),
};

export interface PortalAuthValue {
  me: PortalMe | null;
  signIn: (s: PortalSession) => void;
  signOut: () => void;
}
export const PortalAuthContext = createContext<PortalAuthValue | null>(null);
export function usePortalAuth(): PortalAuthValue {
  const v = useContext(PortalAuthContext);
  if (!v) throw new Error('usePortalAuth fora do PortalProvider');
  return v;
}
